import type { PlanTier } from './billing';

export const UNLIMITED = -1;

export interface PlanLimits {
  maxAssessments: number;
  maxPupils: number;
  maxClasses: number;
  maxTeachers: number;
  collaboration: boolean;
}

export const PLAN_LIMITS: Record<PlanTier, PlanLimits> = {
  free: {
    maxAssessments: 10,
    maxPupils: 10,
    maxClasses: 1,
    maxTeachers: 0,
    collaboration: false,
  },
  individual: {
    maxAssessments: 50,
    maxPupils: 40,
    maxClasses: 2,
    maxTeachers: 0,
    collaboration: false,
  },
  school: {
    maxAssessments: UNLIMITED, // Sub-tier caps applied via SCHOOL_SUB_TIER_ASSESSMENT_LIMITS
    maxPupils: UNLIMITED,
    maxClasses: UNLIMITED,
    maxTeachers: 30,
    collaboration: true,
  },
  trust: {
    maxAssessments: UNLIMITED,
    maxPupils: UNLIMITED,
    maxClasses: UNLIMITED,
    maxTeachers: UNLIMITED,
    collaboration: true,
  },
};

// Monthly assessment caps per school sub-tier (all tiers now have monthly limits)
export const SCHOOL_SUB_TIER_ASSESSMENT_LIMITS: Record<string, number> = {
  starter: 200,
  standard: 500,
  plus: 1000,
};

export function getPlanLimits(tier: PlanTier | null): PlanLimits {
  return PLAN_LIMITS[tier ?? 'free'];
}

function withinLimit(current: number, max: number): boolean {
  return max === UNLIMITED || current < max;
}

// All tiers now have monthly assessment caps (school sub-tiers included).
// Callers must pass the current-month assessment count, not all-time.
export function isMonthlyLimitedTier(_tier: PlanTier | null): boolean {
  return true;
}

// Returns the effective monthly assessment cap for a given tier + sub-tier combination.
export function getMaxAssessments(tier: PlanTier | null, subTier?: string | null): number {
  if (tier === 'school' && subTier) {
    return SCHOOL_SUB_TIER_ASSESSMENT_LIMITS[subTier] ?? UNLIMITED;
  }
  return getPlanLimits(tier).maxAssessments;
}

// Free and individual plans are hard-locked when the cap is reached.
// School plans show a soft upgrade prompt but enforcement is still applied server-side.
export function isHardAssessmentLimit(tier: PlanTier | null): boolean {
  return tier === 'free' || tier === 'individual' || tier === null;
}

export function canCreateAssessment(tier: PlanTier | null, currentMonthCount: number, subTier?: string | null): boolean {
  return withinLimit(currentMonthCount, getMaxAssessments(tier, subTier));
}

export function canCreatePupil(tier: PlanTier | null, currentCount: number): boolean {
  return withinLimit(currentCount, getPlanLimits(tier).maxPupils);
}

export function canCreateClass(tier: PlanTier | null, currentCount: number): boolean {
  return withinLimit(currentCount, getPlanLimits(tier).maxClasses);
}

export function canInviteUser(tier: PlanTier | null): boolean {
  return getPlanLimits(tier).collaboration;
}

export function canUseCollaboration(tier: PlanTier | null): boolean {
  return getPlanLimits(tier).collaboration;
}

export function planLimitMessage(
  resource: 'assessment' | 'pupil' | 'class' | 'teacher',
  max: number,
  tier: PlanTier | null,
): string {
  const tierLabel = tier === 'trust' ? 'Trust' : tier === 'school' ? 'School' : tier === 'individual' ? 'Individual' : 'Free';
  const resourceLabel =
    resource === 'assessment' ? 'assessments this month' :
    resource === 'pupil' ? 'pupils' :
    resource === 'class' ? 'classes' : 'teachers';
  return `You've reached the ${max} ${resourceLabel} limit on your ${tierLabel} plan. Upgrade to continue.`;
}

export function collaborationBlockedMessage(): string {
  return 'Teacher invitations are only available on School plans. Upgrade to collaborate with colleagues.';
}
